import { shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from './auth.store';
import fluidService from '@/shared/services/fluid.service';
import { FLUID_ACTION } from '@/features/fluid/constants/reading';
import { FLUID_PROMPT_KIND } from '@/features/fluid/constants/notification';

// Fluid screens and notification dialogs share accepted clinical state, never preview records.
export const useFluidStore = defineStore('fluidStore', () => {
  const auth = useAuthStore();
  const dashboard = shallowRef(null);
  const reminders = shallowRef([]);
  const prompt = shallowRef(null);
  let dashboardRequest = 0;
  let reminderRequest = 0;

  function showPrompt(context, data) {
    // One root dialog owns clinical prompts across saves, the inbox and native receipt.
    if (
      prompt.value?.kind === FLUID_PROMPT_KIND.NOTIFICATION &&
      prompt.value.context.notificationCode === context.notificationCode &&
      prompt.value.data.refUnique === data.refUnique &&
      prompt.value.data.scheduledAtUtc === data.scheduledAtUtc
    ) {
      return;
    }
    prompt.value = { kind: FLUID_PROMPT_KIND.NOTIFICATION, context, data };
  }

  function showReadingFollowup(result) {
    const assessment = result.followUp ?? result.dashboard.assessment;
    if (assessment.action !== FLUID_ACTION.NONE) {
      prompt.value = { kind: FLUID_PROMPT_KIND.READING, context: result };
    }
  }

  function closePrompt() {
    prompt.value = null;
  }

  function applyDashboard(value, version = auth.authStateVersion) {
    if (version === auth.authStateVersion) {
      dashboardRequest += 1;
      dashboard.value = value;
    }
  }

  function applyReminders(value, version = auth.authStateVersion) {
    if (version === auth.authStateVersion) {
      reminderRequest += 1;
      reminders.value = value;
    }
  }

  async function load() {
    const version = auth.authStateVersion;
    const request = ++dashboardRequest;
    const value = await fluidService.getDashboard();
    // A later read or accepted save owns the state, even when this response is slower.
    if (request === dashboardRequest) {
      applyDashboard(value, version);
    }
  }

  async function loadReminders() {
    const version = auth.authStateVersion;
    const request = ++reminderRequest;
    const value = await fluidService.getReminders();
    if (request === reminderRequest) {
      applyReminders(value, version);
    }
  }

  // Clear immediately on logout/switch; late responses may not repopulate another session.
  watch(
    () => auth.authStateVersion,
    () => {
      dashboard.value = null;
      reminders.value = [];
      prompt.value = null;
    },
    { flush: 'sync' },
  );

  return {
    dashboard,
    reminders,
    prompt,
    showPrompt,
    showReadingFollowup,
    closePrompt,
    load,
    loadReminders,
    applyDashboard,
    applyReminders,
  };
});
